import { MutationOptions, WithRequired } from '@tanstack/react-query';

import {
  getSignedMediaUploadUrlForUseCase,
  patchFlowMessageSetupSkippedReason,
  patchSPOptimizerCampaignDeliverabilityBlockSetup,
  patchSPOptimizerStore,
  patchSPOptimizerStoreDeliverabilityBlockSetup,
  patchStoreInfo,
  patchUsecaseOnboardingState,
  postAddOrganizationMember,
  postChangeUiEnabledForSPOptimizerStore,
  postCSBrowserSessionCancel,
  postCSBrowserSessionEnd,
  postCSBrowserSessionPresence,
  postCSBrowserSessionRenew,
  postCSBrowserSessionStart,
  postCreateSPOptimizerFlowMessageExperimentFromFlowMessage,
  postCreateSPOptimizerStoreDeliverabilityBlockSetup,
  postCreateSPOptimizerStoreFromFlowMessage,
  postCreateSPOptimizerStoreFromStore,
  postDisconnectKlaviyoAccount,
  postCreateUsecasesFromOnboarding,
  postDataQualityAssignDealStore,
  postDataQualityAssignProductLabel,
  postDataQualityAssignSubscriptionStore,
  postDataQualityLinkDealSubscription,
  postDidUploadUsecaseMedia,
  postDisableSPOptimizerFlowMessageExperiment,
  postEnableSPOptimizerFlowMessageExperiment,
  postFeedback,
  postRemoveOrganizationMember,
  postSlackAnnouncement,
  postSaveSelectedProduct,
  postStoreInfo,
  postUpdateOrganizationMemberRole,
  setCampaignSetupAutomationPauseStatus,
} from './methods';

const makeMutation = <F extends (...a: [...Parameters<F>]) => Promise<Awaited<ReturnType<F>>>>(
  apiMethod: F,
  otherOptions?: Omit<MutationOptions<Awaited<ReturnType<F>>, unknown, Parameters<F>>, 'mutationFn'>,
): WithRequired<MutationOptions<Awaited<ReturnType<F>>, unknown, Parameters<F>>, 'mutationFn'> => {
  return {
    mutationFn: (args) => apiMethod(...args),
    ...otherOptions,
  };
};

export const mutations = {
  startCSBrowserSession: makeMutation(postCSBrowserSessionStart),
  endCSBrowserSession: makeMutation(postCSBrowserSessionEnd),
  cancelCSBrowserSession: makeMutation(postCSBrowserSessionCancel),
  renewCSBrowserSession: makeMutation(postCSBrowserSessionRenew),
  setCSBrowserSessionControl: makeMutation(postCSBrowserSessionPresence),
  signedMediaUploadUrlForUseCase: makeMutation(getSignedMediaUploadUrlForUseCase),
  didUploadUseCaseMedia: makeMutation(postDidUploadUsecaseMedia),
  usecaseOnboardingState: makeMutation(patchUsecaseOnboardingState),
  createSPOptimizerStoreFromFlowMessage: makeMutation(postCreateSPOptimizerStoreFromFlowMessage),
  createSPOptimizerStoreFromStore: makeMutation(postCreateSPOptimizerStoreFromStore),
  createSPOptimizerStoreDeliverabilityBlockSetup: makeMutation(postCreateSPOptimizerStoreDeliverabilityBlockSetup),
  updateSPOptimizerStoreDeliverabilityBlockSetup: makeMutation(patchSPOptimizerStoreDeliverabilityBlockSetup),
  createSPOptimizerFlowMessageExperimentFromFlowMessage: makeMutation(
    postCreateSPOptimizerFlowMessageExperimentFromFlowMessage,
  ),
  enableSPOptimizerFlowMessageExperiment: makeMutation(postEnableSPOptimizerFlowMessageExperiment),
  disableSPOptimizerFlowMessageExperiment: makeMutation(postDisableSPOptimizerFlowMessageExperiment),
  updateSPOptimizerStoreUiEnabled: makeMutation(postChangeUiEnabledForSPOptimizerStore),
  disconnectKlaviyoAccount: makeMutation(postDisconnectKlaviyoAccount),
  createStoreInfo: makeMutation(postStoreInfo),
  updateStoreInfo: makeMutation(patchStoreInfo),
  updateSPOptimizerStore: makeMutation(patchSPOptimizerStore),
  createUseCasesFromOnboarding: makeMutation(postCreateUsecasesFromOnboarding),
  createFeedback: makeMutation(postFeedback),
  saveSelectedProduct: makeMutation(postSaveSelectedProduct),
  updateFlowMessageSetupSkippedReason: makeMutation(patchFlowMessageSetupSkippedReason),
  updateSPOptimizerCampaignDeliverabilityBlockSetup: makeMutation(patchSPOptimizerCampaignDeliverabilityBlockSetup),
  addOrganizationMember: makeMutation(postAddOrganizationMember),
  removeOrganizationMember: makeMutation(postRemoveOrganizationMember),
  updateOrganizationMemberRole: makeMutation(postUpdateOrganizationMemberRole),
  setCampaignSetupAutomationPauseStatus: makeMutation(setCampaignSetupAutomationPauseStatus),
  createSlackAnnouncement: makeMutation(postSlackAnnouncement),
  assignDataQualitySubscriptionStore: makeMutation(postDataQualityAssignSubscriptionStore),
  assignDataQualityDealStore: makeMutation(postDataQualityAssignDealStore),
  assignDataQualityProductLabel: makeMutation(postDataQualityAssignProductLabel),
  linkDataQualityDealSubscription: makeMutation(postDataQualityLinkDealSubscription),
  // oxlint-disable-next-line typescript/no-explicit-any
} satisfies Record<string, WithRequired<MutationOptions<any, unknown, any>, 'mutationFn'>>;
